import React, {FC, useCallback, useMemo, useState} from 'react';
import {Typography} from '@components/Typography';
import {ContentBox} from '@components/ContentBox';
import classNames from 'classnames';
import {Button} from '@components/Button';
import {VisuallyHidden} from '@components/VisuallyHidden';
import {Icon} from '@components/Icon';
import {Box} from '@components/Box';
import {TabsNav} from '@components/TabsNav';
import {Form} from '@components/Form';
import {InfoText} from '@components/InfoText';
import {useToggle} from '@hooks/useToggle';
import {FormattedMessage, useIntl} from 'react-intl';
import {Formik} from 'formik';
import {Input} from '../Field';
import * as Yup from 'yup';
import {PhoneInput} from '../Field/PhoneInput';
import {PasswordInput} from '../Field/PasswordInput';
import {Col} from '@components/Col';
import {Row} from '@components/Row';
import {ToggleButton} from '@components/ToggleButton';
import {translations} from '@constants/UserGroupKeyType';
import {entries, LocaleEnum} from 'src/types/general';
import {SelectInput} from '../Field/SelectInput';
import {useRegisterMutation} from '@gql/mutation/user/register.gql';
import {RegistrationType, PageInfoType} from '.cache/__types__';
import {getGraphQlLocale} from 'src/types/graphql/general';
import {useLocale} from '@utils/locale';
import Link from 'next/link';
import {getLocalizedRoute, routes} from '@utils/routes';
import {usePageInfoQuery} from '@gql/query/page/info.gql';
import {useToaster} from '@utils/toast';
import {CheckboxInput} from '@liveComponents/Field/CheckboxInput';
import {InfoTooltip} from '@components/InfoTooltip';
import {useMQ} from '@hooks/useMQ';

export type RegistrationFormProps = {
    onSuccess?: () => any;
    onLogin?: () => any;
    facebookAccessToken?: string;
    googleIdToken?: string;
    showInsertTips?: boolean;
};

export const RegistrationForm: FC<RegistrationFormProps> = ({onSuccess, onLogin, googleIdToken, facebookAccessToken, showInsertTips}) => {
    const intl = useIntl();
    const toaster = useToaster();
    const isMdDown = useMQ('mdDown');
    const [isOpened, toggleOpened] = useToggle(false);
    const [isCompany, setCompany] = useState<boolean>(false);
    const [isOwner, setOwner] = useState<boolean>(false);
    const locale = useLocale();

    const [register] = useRegisterMutation();
    const {data: termsData} = usePageInfoQuery({
        variables: {
            locale: getGraphQlLocale(locale),
            type: PageInfoType.TermsAndConditions,
        },
    });
    const {data: privacyPolicyData} = usePageInfoQuery({
        variables: {
            locale: getGraphQlLocale(locale),
            type: PageInfoType.PrivacyPolicy,
        },
    });

    const processSubmit = useCallback(
        (values, actions) => {
            register({
                variables: {
                    registration: {
                        type: isCompany ? RegistrationType.Legal : RegistrationType.Private,
                        isOwner,
                        broker: true,
                        conditions: values.terms,
                        newsletter: values.newsletter,
                        firstname: values.name,
                        lastname: values.surname,
                        email: values.email,
                        phone: values.phone,
                        password: values.password,
                        companyIc: isCompany ? values.identificationNumber : undefined,
                        companyDic: isCompany ? values.vatNumber : undefined,
                        companyName: isCompany ? values.companyName : undefined,
                        companyType: isCompany ? values.companyType : undefined,
                        street: isCompany ? values.companyStreet : undefined,
                        houseNumber: isCompany ? values.companyStreetNumber : undefined,
                        city: isCompany ? values.companyCity : undefined,
                        zip: isCompany ? values.companyZip : undefined,
                        locale: getGraphQlLocale(locale),
                        googleIdToken,
                        facebookAccessToken,
                    },
                },
            })
                .then(({data}) => {
                    if (data?.registration?.status) {
                        onSuccess && onSuccess();
                    } else if (data?.registration?.errors && data.registration.errors.length > 0) {
                        const error = data.registration.errors?.at(0);
                        toaster.toast({
                            bg: 'danger',
                            color: 'red',
                            message: error?.message,
                            header: <FormattedMessage defaultMessage={'Chyba'} />,
                            duration: 5000,
                        });
                    }
                })
                .catch((error) => {
                    toaster.toast({
                        bg: 'danger',
                        color: 'red',
                        message: error.message,
                        header: <FormattedMessage defaultMessage={'Chyba'} />,
                        duration: 5000,
                    });
                })
                .finally(() => {
                    actions.setSubmitting(false);
                });
        },
        [register, isOwner, isCompany, onSuccess, locale, toaster, googleIdToken, facebookAccessToken],
    );

    const requiredMessage = useMemo(() => intl.formatMessage({defaultMessage: 'Povinné pole'}), [intl]);
    let passwordValidation = Yup.string().matches(
        /^(?=.*[\d!@#$%^&*()\\\[\]{}\-_+=~`|:;"'<>,./?])(?=(.*[a-zA-Z]))(?=(.*)).{8,}$/,
        intl.formatMessage({defaultMessage: 'Heslo není dostatečně silné'}),
    );

    if (!facebookAccessToken && !googleIdToken) {
        passwordValidation = passwordValidation.required(requiredMessage);
    }

    return (
        <>
            <Typography as="h2" variant="h4" className="mb-2" textAlign="center">
                <FormattedMessage defaultMessage="Zaregistrujte se" />
            </Typography>
            <Typography as="p" variant="perex" color="grey-dark" className="mb-4" textAlign="center">
                <FormattedMessage
                    defaultMessage={'Sdělte nám jen pár údajů o vás a můžete začít využívat <toggle>výhod</toggle>.'}
                    values={{
                        toggle: (chunks: any) => (
                            <a
                                className={'link'}
                                role={'button'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    toggleOpened();
                                }}
                            >
                                {chunks}
                            </a>
                        ),
                    }}
                />
            </Typography>

            <ContentBox
                background="grey-light"
                className={classNames('mb-10 mx-n10 px-10 position-relative', `${isOpened ? 'd-block' : 'd-none'}`)}
                rounded={false}
            >
                <Button variant="icon" size="sm" onClick={toggleOpened} className="position-absolute top-0 end-0">
                    <VisuallyHidden>
                        <FormattedMessage defaultMessage={'Zavřít'} />
                    </VisuallyHidden>
                    <Icon.Close position="middle" />
                </Button>
                <Box className="mb-4">
                    <Icon name="CheckCircle" className="me-4" />
                    <Typography>
                        <FormattedMessage defaultMessage="Můžete odepisovat na nejnovější inzeráty" />
                    </Typography>
                </Box>
                <Box className="my-4">
                    <Icon name="CheckCircle" className="me-4" />
                    <Typography>
                        <FormattedMessage defaultMessage={'Hlídací pes, který vás upozorní na nové nabídky'} />
                    </Typography>
                </Box>
                <Box className="my-4">
                    <Icon name="CheckCircle" className="me-4" />
                    <Typography>
                        <FormattedMessage defaultMessage={'Přidávat si vysněné nabídky do oblíbených'} />
                    </Typography>
                </Box>
                <Box className="my-4">
                    <Icon name="CheckCircle" className="me-4" />
                    <Typography>
                        <FormattedMessage defaultMessage={'Vkládat inzeráty'} />
                    </Typography>
                </Box>
            </ContentBox>

            <TabsNav align="center" className="mb-6">
                <TabsNav.Button active={!isCompany} onClick={() => setCompany(false)}>
                    <FormattedMessage defaultMessage={'Fyzická osoba'} />
                </TabsNav.Button>
                <TabsNav.Button active={isCompany} onClick={() => setCompany(true)}>
                    <FormattedMessage defaultMessage={'Právnická osoba'} />
                </TabsNav.Button>
            </TabsNav>

            <Row className="g-3 mb-8">
                <Col xs={6}>
                    <ToggleButton
                        size="lg"
                        type="radio"
                        name="need-help"
                        id="need-help-no"
                        value="1"
                        checked={!isOwner}
                        onChange={() => setOwner(false)}
                    >
                        <Box as="span" className="d-block w-100 text-start">
                            <Typography className="d-block" textTransform="uppercase" textAlign="center">
                                <FormattedMessage defaultMessage={'Jsem zájemce'} />
                            </Typography>
                            <Typography className="d-block mt-2" variant="perex" color="grey-dark" textAlign="center">
                                <FormattedMessage defaultMessage="Hledám nemovitost" />
                            </Typography>
                        </Box>
                    </ToggleButton>
                </Col>

                <Col xs={6}>
                    <ToggleButton
                        size="lg"
                        type="radio"
                        name="need-help"
                        id="need-help-yes"
                        value="2"
                        checked={isOwner}
                        onChange={() => setOwner(true)}
                    >
                        <Box as="span" className="d-block w-100 text-start">
                            <Typography className="d-block" textTransform="uppercase" textAlign="center">
                                <FormattedMessage defaultMessage={'Jsem majitel'} />
                            </Typography>
                            <Typography className="d-block mt-2" variant="perex" color="grey-dark" textAlign="center">
                                <FormattedMessage defaultMessage={'Nabízím nemovitost'} />
                            </Typography>
                        </Box>
                    </ToggleButton>
                </Col>
            </Row>

            <Formik
                onSubmit={processSubmit}
                initialValues={{
                    name: '',
                    surname: '',
                    email: '',
                    phone: '',
                    password: '',
                    identificationNumber: '',
                    vatNumber: '',
                    companyName: '',
                    companyType: '',
                    companyStreet: '',
                    companyStreetNumber: '',
                    companyCity: '',
                    companyZip: '',
                    terms: false,
                    newsletter: false,
                }}
                validationSchema={Yup.object({
                    name: Yup.string().required(requiredMessage),
                    surname: Yup.string().required(requiredMessage),
                    phone: Yup.string()
                        .phone('CZ', intl.formatMessage({defaultMessage: 'Neplatný formát'}))
                        .required(requiredMessage),
                    email: Yup.string()
                        .email(intl.formatMessage({defaultMessage: 'Neplatný email'}))
                        .required(requiredMessage),
                    password: passwordValidation,
                    identificationNumber: isCompany ? Yup.number().required(requiredMessage) : Yup.number(),
                    vatNumber: Yup.string(),
                    companyName: isCompany ? Yup.string().required(requiredMessage) : Yup.string(),
                    companyType: isCompany ? Yup.string().required(requiredMessage) : Yup.string(),
                    companyStreet: isCompany ? Yup.string().required(requiredMessage) : Yup.string(),
                    companyStreetNumber: isCompany ? Yup.string().required(requiredMessage) : Yup.string(),
                    companyCity: isCompany ? Yup.string().required(requiredMessage) : Yup.string(),
                    companyZip: isCompany ? Yup.string().required(requiredMessage) : Yup.string(),
                    terms: Yup.boolean().oneOf([true], requiredMessage),
                    newsletter: Yup.boolean(),
                })}
            >
                {(props) => (
                    <Form onSubmit={props.handleSubmit} noValidate>
                        {isCompany && (
                            <>
                                <Input
                                    className={'mb-4'}
                                    name={'identificationNumber'}
                                    type={'number'}
                                    placeholder={intl.formatMessage({defaultMessage: 'IČ'})}
                                />

                                <Input className={'mb-4'} name={'vatNumber'} placeholder={intl.formatMessage({defaultMessage: 'DIČ'})} />

                                <Input
                                    className={'mb-4'}
                                    name={'companyName'}
                                    autoComplete={'organization'}
                                    placeholder={intl.formatMessage({defaultMessage: 'Název právnické osoby'})}
                                />

                                <SelectInput
                                    options={entries(translations).map(([key, value]) => ({value: key, name: value}))}
                                    className={'mb-4'}
                                    name={'companyType'}
                                    placeholder={intl.formatMessage({defaultMessage: 'Typ právnické osoby'})}
                                />

                                <Input
                                    className={'mb-4'}
                                    name={'companyStreet'}
                                    placeholder={intl.formatMessage({defaultMessage: 'Ulice'})}
                                />

                                <Input
                                    className={'mb-4'}
                                    name={'companyStreetNumber'}
                                    placeholder={intl.formatMessage({defaultMessage: 'Číslo popisné'})}
                                />

                                <Input
                                    className={'mb-4'}
                                    name={'companyCity'}
                                    placeholder={intl.formatMessage({defaultMessage: 'Město'})}
                                />

                                <Input className={'mb-4'} name={'companyZip'} placeholder={intl.formatMessage({defaultMessage: 'PSČ'})} />
                            </>
                        )}

                        <Input
                            className={'mb-4'}
                            name={'name'}
                            autoComplete={'given-name'}
                            placeholder={intl.formatMessage({defaultMessage: 'Jméno'})}
                        />

                        <Input
                            className={'mb-4'}
                            name={'surname'}
                            autoComplete={'family-name'}
                            placeholder={intl.formatMessage({defaultMessage: 'Příjmení'})}
                        />

                        <PhoneInput className={'mb-4'} name={'phone'} placeholder={intl.formatMessage({defaultMessage: 'Telefon'})} />

                        <Input
                            className={'mb-4'}
                            name={'email'}
                            autoComplete={'email'}
                            placeholder={intl.formatMessage({defaultMessage: 'Vaše e-mailová adresa'})}
                        />

                        {showInsertTips && (
                            <InfoText className="mb-4">
                                <FormattedMessage
                                    defaultMessage={
                                        'Tyto údaje zájemci neuvidí a budou vás kontaktovat prostřednictvím komunikační platformy'
                                    }
                                />
                            </InfoText>
                        )}

                        <PasswordInput
                            className={'mb-4'}
                            name={'password'}
                            autoComplete={'new-password'}
                            placeholder={intl.formatMessage({defaultMessage: 'Vaše heslo'})}
                        />

                        {showInsertTips && (
                            <InfoText className="mb-4">
                                <FormattedMessage defaultMessage={'Abychom mohli váš inzerát uložit, zadejte prosím heslo'} />
                            </InfoText>
                        )}

                        <InfoText className="mb-4">
                            <FormattedMessage defaultMessage={'Silné heslo (Alespoň 8 znaků, speciální symbol nebo číslo)'} />
                        </InfoText>

                        {googleIdToken && (
                            <InfoText className="mb-4">
                                <FormattedMessage
                                    defaultMessage={
                                        'Váš účet bude automaticky propojen s vaším Google účtem. Heslo v tomto případě není povinné.'
                                    }
                                />
                            </InfoText>
                        )}

                        {facebookAccessToken && (
                            <InfoText className="mb-4">
                                <FormattedMessage
                                    defaultMessage={
                                        'Váš účet bude automaticky propojen s vaším Facebook účtem. Heslo v tomto případě není povinné.'
                                    }
                                />
                            </InfoText>
                        )}

                        {termsData && privacyPolicyData && (
                            <CheckboxInput
                                label={
                                    <FormattedMessage
                                        defaultMessage="Souhlasím se <terms>Smluvními podmínkami</terms> a <privacy>Zásadami pro ochranu osobních údajů</privacy>, zároveň potvrzuji, že nejsem realitní makléř."
                                        values={{
                                            terms: (chunks: any) => (
                                                <Link
                                                    href={getLocalizedRoute(routes.page, locale, {
                                                        uri: [termsData.pageInfo?.uri, termsData.pageInfo?.suburi]
                                                            .filter((d) => !!d)
                                                            .join('/'),
                                                    })}
                                                >
                                                    <a target="_blank">{chunks}</a>
                                                </Link>
                                            ),
                                            privacy: (chunks: any) => (
                                                <Link
                                                    href={getLocalizedRoute(routes.page, locale, {
                                                        uri: [privacyPolicyData.pageInfo?.uri, privacyPolicyData.pageInfo?.suburi]
                                                            .filter((d) => !!d)
                                                            .join('/'),
                                                    })}
                                                >
                                                    <a target="_blank">{chunks}</a>
                                                </Link>
                                            ),
                                        }}
                                    />
                                }
                                name={'terms'}
                                className={'mb-3'}
                            />
                        )}

                        {locale === LocaleEnum.cs && (
                            <InfoTooltip
                                trigger={isMdDown ? ['hover', 'focus', 'click'] : 'click'}
                                content={
                                    <FormattedMessage
                                        defaultMessage={
                                            'Odesláním souhlasíte se zpracováním osobních údajů za účelem nabízení a zpracováním marketingových nabídek společnosti Bezrealitky s.r.o. Kdykoliv máte právo svůj souhlas odvolat. Více informací v <link>zásadách zpracování osobních údajů</link>.'
                                        }
                                        values={{
                                            link: (chunks: any) => (
                                                <Link
                                                    href={getLocalizedRoute(routes.page, locale, {
                                                        uri: 'smluvni-podminky/zasady-zpracovani-osobnich-udaju-marketing',
                                                    })}
                                                    passHref
                                                >
                                                    <a>{chunks}</a>
                                                </Link>
                                            ),
                                        }}
                                    />
                                }
                            >
                                <CheckboxInput
                                    label={
                                        <FormattedMessage
                                            defaultMessage={'Chci získat nejzajímavější nabídky a novinky z realitního trhu.'}
                                        />
                                    }
                                    name={'newsletter'}
                                    className={'mb-3'}
                                />
                            </InfoTooltip>
                        )}

                        {termsData && privacyPolicyData && (
                            <Button type={'submit'} className="w-100">
                                <FormattedMessage defaultMessage={'Pokračovat'} />
                            </Button>
                        )}
                    </Form>
                )}
            </Formik>

            {onLogin && (
                <Box className="d-flex justify-content-between mt-4">
                    <InfoText iconName={false} className="mb-0">
                        <FormattedMessage
                            defaultMessage={'Již máte účet? <link>Přihlásit se</link>'}
                            values={{
                                link: (chunks: any) => (
                                    <a role={'button'} onClick={onLogin} className={'link'}>
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    </InfoText>
                </Box>
            )}
        </>
    );
};
